<template>
  <GlPageWrap>
    <div class="profile-wrapper">
      <CardContainer class="mt-4">
        <CardHeader />

        <Section class="mb-4">
          <SectionTitle />
          <InfoRow>
            <InfoText :value="userData.email" />
          </InfoRow>
          <GlButton
            v-if="featureAccess('CLEAR_ONBOARDING_STATE')"
            pale-light
            size="medium"
            title="Clear Onboarding State"
            @click="deleteOnboardingState"
          />
        </Section>

        <Section v-if="isFreePlan">
          <SectionTitle text="Free Plan" />
          <div class="mb-3">
            Upgrade to explore more insights
          </div>
          <div>
            <GlButton
              gradient
              size="medium"
              title="Change Plan"
              @click="goToChangePlan"
            >
              <template #beforeTitle>
                <gl-icon
                  class="upgrade-plan-gradient"
                  :height="24"
                  name="I-Upgrade-Plan"
                  white
                  :width="24"
                />
              </template>
            </GlButton>
          </div>
        </Section>

        <Section
          v-else
          class="mb-4"
        >
          <SectionTitle :text="planTitleText" />

          <PlanPriceLine
            v-if="!isB2bUser"
            class="mb-3"
            :prefix="priceLinePrefix"
            :price="priceLineValue"
            :suffix="priceLineSuffix"
          />

          <template v-if="isActiveOrTrial">
            <template v-if="isTrialActive">
              <SectionSubTitle
                class="mb-2"
                text="During Trial"
              />
              <UsageRow
                label="Reports left"
                :value="currentUsageReports"
              />
              <UsageRow
                v-if="!currentUsageRefreshes"
                class="mb-3"
                label="Free data updates per report"
                :value="currentUsageRefreshes"
              />

              <SectionSubTitle
                class="mb-2"
                text="Then"
              />
            </template>

            <UsageRow
              label="Reports left"
              :value="baseReportsLimit"
            >
              <template
                v-if="!safePlan.reportsRemaining && !isTrialActive && !isB2bUser"
                #extra-info
              >
                <gl-icon
                  failure
                  :height="20"
                  name="I-S-Info-Fill"
                  :width="20"
                  @click="onLimitClick"
                />
              </template>
            </UsageRow>
            <UsageRow
              v-if="baseRefreshesLimit && !isB2bUser"
              class="mb-3"
              label="Free data updates per report"
              :value="baseRefreshesLimit"
            />
          </template>
        </Section>

        <Section v-if="!isFreePlan">
          <ButtonGroup>
            <GlButton
              v-if="isTrialActive"
              :loading="loadingActivateTrial"
              pale-light
              size="medium"
              title="End Trial & Unlock Full Access"
              @click="handleShowActivateTrial"
            />
            <GlButton
              v-if="showRefreshQuotaButton"
              :loading="loadingOnRefreshQuota"
              pale-light
              size="medium"
              title="Refresh Report Limits"
              @click="handleRefreshQuota"
            >
              <template #beforeTitle>
                <gl-icon
                  gray
                  :height="24"
                  name="I-Update"
                  :width="24"
                />
              </template>
            </GlButton>

            <GlButton
              v-if="pendingCancelActionText"
              :loading="loadingOnCancelDowngrade"
              pale-light
              size="medium"
              :title="pendingCancelActionText"
              @click="handleCancelDowngrade"
            >
              <template #beforeTitle>
                <gl-icon
                  gray
                  :height="14"
                  name="I-Close"
                  :width="16"
                />
              </template>
            </GlButton>

            <GlButton
              v-if="!isB2bUser"
              pale-light
              size="medium"
              title="Change Plan"
              @click="goToChangePlan"
            >
              <template #beforeTitle>
                <gl-icon
                  class="upgrade-plan-gradient"
                  :height="24"
                  name="I-Upgrade-Plan-Gradient"
                  :width="24"
                />
              </template>
            </GlButton>

            <GlButton
              v-if="!isB2bUser"
              :loading="loadingOnRequest"
              pale-light
              size="medium"
              title="Manage Subscription"
              @click="goToManageSubscription"
            />
          </ButtonGroup>
        </Section>
      </CardContainer>
    </div>
    <ConfirmPlanActionModal
      v-if="refreshPackageData"
      v-model="showConfirmRefreshModal"
      action-status="refresh-quota"
      :current-plan="currentPlan"
      :loading="loadingOnRefreshQuota"
      :plan="refreshPackageData"
      @close="showConfirmRefreshModal = false"
      @confirm="executeRefreshQuota"
    />
  </GlPageWrap>
</template>

<script>
// Vuex
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';

// Components
import GlIcon from '@/components/gl-icon';
import GlButton from "@/components/gl-button.vue";
import GlPageWrap from '@/components/layout/gl-page-wrap';
import Section from '@/pages/profile/account/Section.vue';
import InfoRow from '@/pages/profile/account/InfoRow.vue';
import InfoText from '@/pages/profile/account/InfoText.vue';
import UsageRow from '@/pages/profile/account/UsageRow.vue';
import CardHeader from '@/pages/profile/account/CardHeader.vue';
import SectionTitle from '@/pages/profile/account/SectionTitle.vue';
import ButtonGroup from '@/pages/profile/account/ButtonGroup.vue';
import CardContainer from '@/pages/profile/account/CardContainer.vue';
import PlanPriceLine from '@/pages/profile/account/PlanPriceLine.vue';
import SectionSubTitle from "@/pages/profile/account/SectionSubTitle.vue";
import StripeModal2 from "@/pages/payment/3rd-party/stripe/StripeModal2.vue";
import ConfirmPlanActionModal from "@/pages/profile/modals/ConfirmPlanActionModal.vue";
import LimitsReachedModal from "@/pages/report/components/purchase/modals/LimitsReachedModal.vue";

// Utils
import {featureAccess} from "@/utils/accesses";
import {deleteOnboardingState} from "@/services/onboarding-tour";
import { createPlansList, formatPlanData } from '@/utils/plans';
import { openBillingPortal } from '@/services/billing/subscription'

export default {
  name: 'SubscriptionProfilePage',
  components: {
    LimitsReachedModal,
    GlPageWrap,
    GlIcon,
    GlButton,
    CardContainer,
    PlanPriceLine,
    CardHeader,
    Section,
    SectionTitle,
    SectionSubTitle,
    InfoRow,
    InfoText,
    UsageRow,
    ButtonGroup,
    StripeModal2,
    ConfirmPlanActionModal
  },
  data: () => ({
    loadingOnRequest: false,
    loadingOnCancelDowngrade: false,
    loadingOnRefreshQuota: false,
    loadingActivateTrial: false,
    showConfirmRefreshModal: false,
    showLimitsReachedModal: false,
    refreshPackageData: null,
  }),
  computed: {
    ...mapState('user', ['userData']),
    ...mapState('payment', ['currentPlan']),
    ...mapGetters('payment', ['getPlans']),
    ...mapGetters('user', ['isB2bUser']),
    safePlan() {
      return this.currentPlan || {};
    },
    isAnnual() {
      return this.safePlan.billingCycle === 'annual';
    },
    isActiveOrTrial() {
      return this.safePlan.status === 'active' || this.safePlan.status === 'trialing' || this.safePlan.status === 'cancel_pending';
    },
    isFreePlan() {
      return !this.currentPlan || this.safePlan.status === 'none' || this.safePlan.planTier === 'free';
    },
    isTrialActive() {
      const { status, trialEnd } = this.safePlan;
      return status === 'trialing' || !!(trialEnd && new Date(trialEnd) > new Date());
    },
    targetPlanData() {
      if (!this.getPlans || this.isFreePlan) return null;
      return this.getPlans.find(p => p.tier === this.safePlan.planTier) || null;
    },
    planTitleText() {
      if (this.isFreePlan) return 'Free Plan';
      const name = this.targetPlanData?.name || 'Plan';

      if (this.isB2bUser) return `${name} Plan`;

      if (this.isTrialActive && this.safePlan.trialEnd) {
        const diffDays = Math.round((new Date(this.safePlan.trialEnd) - new Date()) / (1000 * 60 * 60 * 24));
        const displayDays = diffDays > 0 ? diffDays : 1;

        return `${name} Plan · ${displayDays} day free left`;
      }

      const cycle = this.isAnnual ? 'year' : 'month';
      const priceKey = this.isAnnual ? 'annual' : 'monthly';
      const price = this.targetPlanData?.prices?.[priceKey] || 0;

      return `${name} Plan · €${price} / ${cycle}`;
    },
    priceLinePrefix() {
      return this.isTrialActive ? 'Then' : '';
    },
    priceLineValue() {
      if (!this.isTrialActive || !this.targetPlanData) return '';
      const cycle = this.isAnnual ? 'year' : 'month';
      const priceKey = this.isAnnual ? 'annual' : 'monthly';
      const price = this.targetPlanData.prices?.[priceKey] || 0;
      return `€${price} / ${cycle}`;
    },
    priceLineSuffix() {
      const dateSource = this.isTrialActive ? this.safePlan.trialEnd : this.safePlan.currentPeriodEnd;
      if (!dateSource) return '';

      const dateStr = new Date(dateSource).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });

      if (this.isTrialActive) return `after ${dateStr}`;

      const { pendingDowngradeCycle, billingCycle, pendingDowngradeTier, cancelAtPeriodEnd } = this.safePlan;

      if (cancelAtPeriodEnd) return `Plan will end on ${dateStr}`;

      if (pendingDowngradeCycle && pendingDowngradeCycle !== billingCycle) {
        const cycleName = pendingDowngradeCycle === 'annual' ? 'annually' : 'monthly';
        return `Will switch to ${cycleName} billing on ${dateStr}`;
      }

      if (pendingDowngradeTier) {
        const tierName = pendingDowngradeTier.charAt(0).toUpperCase() + pendingDowngradeTier.slice(1);
        return `Downgrade to ${tierName} Plan scheduled for ${dateStr}`;
      }

      return `Next payment: ${dateStr}`;
    },
    currentUsageReports() {
      const rem = this.safePlan.reportsRemaining || 0;
      const quota = this.safePlan.monthlyQuota || 0;
      return `${rem} / ${(!quota || quota === -1) ? 'Unlimited' : String(quota)}`;
    },
    currentUsageRefreshes() {
      if (!this.targetPlanData?.features?.freeUpdatesPerReport) return 0;
      return `${this.targetPlanData?.features?.freeUpdatesPerReport}`;
    },
    baseReportsLimit() {
      if (this.isB2bUser) return this.currentUsageReports;
      if (this.safePlan.planTier === 'pro') return 'Unlimited';
      if (!this.isTrialActive) return this.currentUsageReports;

      const quota = this.targetPlanData?.features?.monthlyQuota;
      return (!quota || quota === -1) ? 'Unlimited' : String(quota);
    },
    baseRefreshesLimit() {
      if (this.safePlan.planTier === 'pro') return 'Unlimited';
      if (!this.isTrialActive) return this.currentUsageRefreshes;

      const updates = this.targetPlanData?.features?.freeUpdatesPerReport;

      if (!updates) return 0;

      return (updates === undefined || updates === -1) ? 'Unlimited' : Number(updates);
    },
    pendingCancelActionText() {
      const { pendingDowngradeCycle, billingCycle, pendingDowngradeTier } = this.safePlan;
      if (pendingDowngradeCycle && pendingDowngradeCycle !== billingCycle) return 'Cancel Switch';
      if (pendingDowngradeTier) return 'Cancel Downgrade';
      return '';
    },
    showRefreshQuotaButton() {
      if (this.isFreePlan || this.safePlan.planTier === 'pro' || !this.isActiveOrTrial) return false;
      return this.safePlan.reportsRemaining <= 0 && !this.isTrialActive;
    },
    limitsInfo() {
      const monthlyQuota = this.safePlan?.monthlyQuota
      const reportsRemaining = this.safePlan?.reportsRemaining

      return { reportsRemaining, monthlyQuota }
    }
  },
  methods: {
    deleteOnboardingState,
    featureAccess,
    ...mapActions('modal', ['openModal', 'closeModal']),
    ...mapActions('payment', ['cancelDowngrade', 'fetchCurrentPlan', 'refreshQuota', 'createBillingPortalSession', 'activateTrial']),
    ...mapMutations('payment', ['SET_CURRENT_PLAN']),

    goToChangePlan() {
      this.$router.push({ name: 'changePlan' });
    },
    onLimitClick() {
      const monthlyQuota = this.safePlan?.monthlyQuota
      const reportsRemaining = this.safePlan?.reportsRemaining
      this.openModal({component: LimitsReachedModal, props: { limitInfo: { reportsRemaining, monthlyQuota } } })
    },
    async handleShowActivateTrial() {
      this.openModal({
        component: LimitsReachedModal,
        props: {
          limitInfo: this.limitsInfo,
          onlyEmit: true,
          onConfirmCallback: () => {
            this.closeModal();
            this.handleActiveTrial();
          }
        }
      });
    },
    async handleActiveTrial() {
      this.loadingActivateTrial = true;
      try {
        const returnUrl = window.location.href + (window.location.search ? '&' : '?') + 'payment_success=trial';
        const response = await this.activateTrial({ returnUrl });

        if (response?.data?.redirectUrl) {
          window.location.href = response.data.redirectUrl;
          return;
        }

        if (response?.data?.clientSecret) {
          const rawPlans = this.getPlans || [];
          const plan = createPlansList({ plans: rawPlans }).find(p => p.tier === this.safePlan.planTier);
          const planData = plan ? formatPlanData({
            plan,
            currentPlan: this.safePlan,
            selectedCycle: this.safePlan.billingCycle === 'annual' ? 'annual' : 'month',
            tierWeights: { free: 0, starter: 1, standard: 2, pro: 3 }
          }) : {};

          await this.openModal({
            component: StripeModal2,
            props: {
              insertClientSecret: response.data.clientSecret,
              packageData: { ...planData, _id: planData.id || this.safePlan.planTier },
              closable: true,
            }
          });
        }
      } catch (error) {
        console.error('Trial activation failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.error || error.message || 'Failed to activate full access',
          life: 5000
        });
      } finally {
        this.loadingActivateTrial = false;
      }
    },
    handleRefreshQuota() {
      const monthlyPrice = this.targetPlanData?.prices?.monthly || 0;
      const feeTitle = this.isAnnual ? 'Top-up Fee' : 'Renewal Fee';
      const formattedPrice = `€${monthlyPrice}`;
      const monthlyQuota = this.targetPlanData?.features?.monthlyQuota;
      const displayQuota = (!monthlyQuota || monthlyQuota === -1) ? 'Unlimited' : String(monthlyQuota);
      const restoredLimitText = displayQuota === 'Unlimited'
          ? 'Unlimited'
          : `${displayQuota} / ${displayQuota}`;

      this.refreshPackageData = {
        id: 'refresh-quota',
        planName: feeTitle,
        planDescription: 'Instantly reset your report limits',
        price: formattedPrice,
        totalPrice: formattedPrice,
        billing: 'One-time payment',
        benefits: [
          {
            label: `Restore to ${restoredLimitText} reports`,
            icon: "I-Check",
            iconType: "gray",
            isHtml: false
          }
        ]
      };

      this.showConfirmRefreshModal = true;
    },

    async executeRefreshQuota() {
      this.loadingOnRefreshQuota = true;
      try {
        const returnUrl = window.location.href + (window.location.search ? '&' : '?') + 'payment_success=refresh';
        const response = await this.refreshQuota({ returnUrl });

        if (response?.data?.redirectUrl) {
          this.showConfirmRefreshModal = false;
          window.location.href = response.data.redirectUrl;
          return;
        }

        if (response?.data?.clientSecret) {
          this.showConfirmRefreshModal = false;

          await this.openModal({
            component: StripeModal2,
            props: {
              insertClientSecret: response.data.clientSecret,
              packageData: this.refreshPackageData,
              isRefreshAction: true
            }
          });
        }
        else if (response) {
          this.showConfirmRefreshModal = false;
          await this.syncCurrentPlan();
          this.$toast.add({
            severity: 'success',
            summary: 'Success',
            detail: response.data?.message || 'Report quota has been refreshed.',
            life: 3000
          });
        }
      } catch (error) {
        console.error('Refresh quota failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.error || error.message || 'Failed to refresh quota',
          life: 5000
        });
      } finally {
        this.loadingOnRefreshQuota = false;
      }
    },

    async handleCancelDowngrade() {
      this.loadingOnCancelDowngrade = true;
      try {
        await this.cancelDowngrade();
      } catch (error) {
        console.error(error);
      } finally {
        await this.syncCurrentPlan();
        this.loadingOnCancelDowngrade = false;
      }
    },

    async syncCurrentPlan() {
      const updated = await this.fetchCurrentPlan();
      if (updated?.data) {
        this.SET_CURRENT_PLAN(updated.data);
      }
    },

    async goToManageSubscription() {
      this.loadingOnRequest = true
      await openBillingPortal(this.$store, {
        returnUrl: window.location.href,
        onError: (message) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
          })
        },
      })
      this.loadingOnRequest = false
    },
  },
};
</script>

<style scoped>
.profile-wrapper {
  display: flex;
  justify-content: center;
  background-color: var(--light-grey-f-8);
  padding-top: var(--header-height);
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
}

@media (max-width: 767px) {
  .profile-wrapper {
    background: var(--grey-white);
  }
}
</style>
